// Team invitations for owners and administrators (B6). The server is the authority for who may
// invite whom; this mirrors its delegation rules only to offer sensible choices. "Queued" and
// "Accepted by email provider" are never presented as delivery. Retrying a send reuses the same
// Idempotency-Key, so a lost answer can't create a second invitation. No token is ever shown.
import type { ApiClient } from '../../lib/api.ts';
import { isAbortError, isApiRequestError } from '../../lib/api.ts';
import { type InvitationState, type InvitationView, type RoleId, isEmail, mayAssignRoles, parseInvitation, parseInvitationPage } from '../../identity/contract.ts';
import { type FlowDiagnostic, classifyFailure, diagnosticOf, loadIdentityCapabilities } from '../../identity/flow.ts';

export type InvitationAccess =
  | { kind: 'loading' }
  | { kind: 'missing'; diagnostic: FlowDiagnostic } // not on this server (404 / 503)
  | { kind: 'needs_mfa' } // this session has no verification-code proof
  | { kind: 'denied'; diagnostic: FlowDiagnostic | null }
  | { kind: 'ready'; mail: boolean; mailReason: string | null } // mail=false: listing works, sending doesn't
  | { kind: 'error'; diagnostic: FlowDiagnostic };

export type InvitationFilter = InvitationState | '';

export interface CreateState {
  phase: 'idle' | 'sending' | 'done' | 'error';
  result: InvitationView | null;
  error: string | null;
  /** The last send got no answer: sending the same details again is safe and won't duplicate it. */
  retrySafe: boolean;
  diagnostic: FlowDiagnostic | null;
}

export interface InvitationsSnapshot {
  access: InvitationAccess;
  filter: InvitationFilter;
  items: InvitationView[];
  next: string | null;
  list: 'idle' | 'loading' | 'more' | 'error';
  listNotice: string | null;
  listDiagnostic: FlowDiagnostic | null;
  create: CreateState;
  revoking: string | null;
  rowMessage: { id: string; text: string } | null;
}

export interface InvitationsOptions {
  api: Pick<ApiClient, 'request'>;
  /** The current membership's roles (server-supplied session data). */
  myRoles: readonly string[];
  /** The session proved a verification code at sign-in. */
  hasTotp: boolean;
  pageSize?: number;
  newKey?: () => string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CREATE_IDLE: CreateState = { phase: 'idle', result: null, error: null, retrySafe: false, diagnostic: null };

export class InvitationsController {
  private snap: InvitationsSnapshot = { access: { kind: 'loading' }, filter: '', items: [], next: null, list: 'idle', listNotice: null, listDiagnostic: null, create: CREATE_IDLE, revoking: null, rowMessage: null };
  private readonly listeners = new Set<() => void>();
  private listGen = 0;
  private listAbort: AbortController | null = null;
  private pending: { fingerprint: string; key: string } | null = null;
  private disposed = false;
  private readonly pageSize: number;
  private readonly newKey: () => string;

  constructor(private readonly options: InvitationsOptions) {
    this.pageSize = options.pageSize ?? 30;
    this.newKey = options.newKey ?? (() => `inv-${crypto.randomUUID()}`);
  }

  getSnapshot = (): InvitationsSnapshot => this.snap;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  /** Roles the server would let me grant; the server checks again. */
  mayInvite(roles: readonly RoleId[]): boolean {
    return mayAssignRoles(this.options.myRoles, roles);
  }

  /** Owner invitations can be revoked only by an owner. */
  mayRevoke(item: InvitationView): boolean {
    return !item.role_ids.includes('owner') || this.options.myRoles.includes('owner');
  }

  async start(): Promise<void> {
    this.disposed = false;
    this.set({ access: { kind: 'loading' } });
    const gen = ++this.listGen;
    const caps = await loadIdentityCapabilities(this.options.api);
    if (this.disposed || gen !== this.listGen) return;
    if (caps.kind === 'missing') return this.set({ access: { kind: 'missing', diagnostic: caps.diagnostic } });
    if (caps.kind === 'error') return this.set({ access: { kind: 'error', diagnostic: caps.diagnostic } });
    const inv = caps.value.invitations;
    if (!inv.allowed) return this.set({ access: this.options.hasTotp ? { kind: 'denied', diagnostic: inv.reason_code ? { code: inv.reason_code, requestId: null } : null } : { kind: 'needs_mfa' } });
    this.set({ access: { kind: 'ready', mail: inv.status === 'enabled', mailReason: inv.reason_code } });
    void this.load(this.snap.filter, null);
  }

  dispose(): void {
    this.disposed = true;
    this.listGen += 1;
    this.listAbort?.abort();
    this.listAbort = null;
  }

  /** Changing the filter starts the list over; a cursor never crosses filters. */
  setFilter(filter: InvitationFilter): void {
    if (filter === this.snap.filter && this.snap.list !== 'error') return;
    this.set({ filter, items: [], next: null, listNotice: null, rowMessage: null });
    void this.load(filter, null);
  }

  reload(): void {
    this.set({ items: [], next: null, listNotice: null });
    void this.load(this.snap.filter, null);
  }

  loadMore(): void {
    if (!this.snap.next || this.snap.list !== 'idle') return;
    void this.load(this.snap.filter, this.snap.next);
  }

  private async load(filter: InvitationFilter, cursor: string | null): Promise<void> {
    if (this.snap.access.kind !== 'ready') return;
    const gen = ++this.listGen;
    this.listAbort?.abort();
    const abort = new AbortController();
    this.listAbort = abort;
    this.set({ list: cursor ? 'more' : 'loading', listDiagnostic: null });
    const params = new URLSearchParams();
    if (filter) params.set('state', filter);
    params.set('limit', String(this.pageSize));
    if (cursor) params.set('cursor', cursor);
    try {
      const env = await this.options.api.request<unknown>(`/team/invitations?${params.toString()}`, { signal: abort.signal });
      if (this.disposed || gen !== this.listGen) return;
      this.listAbort = null;
      const page = parseInvitationPage(env);
      if (!page) return this.set({ list: 'error', listDiagnostic: { code: 'invalid_response', requestId: null } });
      const known = new Set(cursor ? this.snap.items.map(i => i.id) : []);
      const items = cursor ? [...this.snap.items, ...page.items.filter(i => !known.has(i.id))] : page.items;
      this.set({ items, next: page.next_cursor, list: 'idle' });
    } catch (error) {
      if (isAbortError(error) || this.disposed || gen !== this.listGen) return;
      this.listAbort = null;
      if (cursor && isApiRequestError(error) && error.code === 'cursor_invalid') {
        // Cursors expire and are bound to the filter: start this filter over.
        this.set({ items: [], next: null, listNotice: 'The list changed, so it was reloaded from the top.' });
        void this.load(filter, null);
        return;
      }
      if (!this.accessFailure(error)) this.set({ list: 'error', listDiagnostic: diagnosticOf(error) });
    }
  }

  /** 403/404/503 answers change what the panel can do at all. True when handled. */
  private accessFailure(error: unknown): boolean {
    if (!isApiRequestError(error)) return false;
    if (error.code === 'mfa_required') {
      this.set({ access: { kind: 'needs_mfa' }, list: 'idle' });
      return true;
    }
    if (error.code === 'permission_denied') {
      this.set({ access: { kind: 'denied', diagnostic: diagnosticOf(error) }, list: 'idle' });
      return true;
    }
    if (error.status === 404 || error.status === 503) {
      this.set({ access: { kind: 'missing', diagnostic: diagnosticOf(error) }, list: 'idle' });
      return true;
    }
    return false;
  }

  async create(emailInput: string, roles: readonly RoleId[]): Promise<void> {
    if (this.snap.access.kind !== 'ready' || this.snap.create.phase === 'sending') return;
    const email = emailInput.trim();
    if (!isEmail(email)) return this.set({ create: { ...CREATE_IDLE, phase: 'error', error: 'Enter a valid email address.' } });
    const sorted = [...roles].sort();
    if (!this.mayInvite(sorted as RoleId[]) || sorted.length > 5) return this.set({ create: { ...CREATE_IDLE, phase: 'error', error: 'Choose at least one role you’re allowed to give.' } });
    if (!this.snap.access.mail) return this.set({ create: { ...CREATE_IDLE, phase: 'error', error: 'Invitation email isn’t set up on this server, so nothing can be sent yet.' } });
    const fingerprint = `${email.toLowerCase()}|${sorted.join(',')}`;
    if (this.pending?.fingerprint !== fingerprint) this.pending = { fingerprint, key: this.newKey() };
    const key = this.pending.key;
    this.set({ create: { ...CREATE_IDLE, phase: 'sending' } });
    try {
      const env = await this.options.api.request<{ data?: unknown }>('/team/invitations', { method: 'POST', body: JSON.stringify({ email, role_ids: sorted }), idempotencyKey: key });
      if (this.disposed) return;
      const view = parseInvitation(env?.data);
      if (!view) {
        // It may exist; the same key makes another try safe.
        return this.set({ create: { ...CREATE_IDLE, phase: 'error', retrySafe: true, error: 'The server’s answer wasn’t readable. Send again to check; it won’t create a second invitation.', diagnostic: { code: 'invalid_response', requestId: null } } });
      }
      this.pending = null;
      this.set({ create: { ...CREATE_IDLE, phase: 'done', result: view }, items: this.withRow(view) });
    } catch (error) {
      if (this.disposed) return;
      const kind = classifyFailure(error);
      const code = isApiRequestError(error) ? error.code : '';
      const diagnostic = diagnosticOf(error);
      if (code === 'idempotency_conflict') this.pending = null;
      if (kind === 'unknown' || kind === 'network' || kind === 'limited' || (isApiRequestError(error) && error.status >= 500 && error.status !== 503)) {
        return this.set({ create: { ...CREATE_IDLE, phase: 'error', retrySafe: kind !== 'limited', diagnostic,
          error: kind === 'limited' ? 'Invitation limit reached. Wait a while, then try again.' : 'No answer from the server, so it may or may not have been created. Send again: the same details won’t create a second invitation.' } });
      }
      if (code === 'identity_mail_unconfigured' && this.snap.access.kind === 'ready') {
        this.set({ access: { ...this.snap.access, mail: false, mailReason: code } });
        return this.set({ create: { ...CREATE_IDLE, phase: 'error', diagnostic, error: 'Invitation email isn’t set up on this server, so nothing was sent.' } });
      }
      if (code === 'mfa_required') {
        this.set({ access: { kind: 'needs_mfa' }, create: CREATE_IDLE });
        return;
      }
      const message =
        code === 'membership_exists' ? 'That person is already a member of this workspace.'
          : code === 'role_escalation_denied' ? 'You can’t give one of those roles.'
            : code === 'idempotency_conflict' ? 'Those details changed since the last try. Check them and send again.'
              : code === 'invalid_email' || kind === 'input' ? 'Enter a valid email address.'
                : code === 'permission_denied' ? 'Your role no longer allows sending invitations.'
                  : 'The invitation wasn’t created. Try again.';
      this.set({ create: { ...CREATE_IDLE, phase: 'error', error: message, diagnostic } });
    }
  }

  /** The form was edited after a result: hide the old result. */
  clearCreate(): void {
    if (this.snap.create.phase === 'done') this.set({ create: CREATE_IDLE });
  }

  async revoke(item: InvitationView): Promise<void> {
    if (this.snap.revoking || !UUID.test(item.id) || !this.mayRevoke(item)) return;
    this.set({ revoking: item.id, rowMessage: null });
    try {
      await this.options.api.request<void>(`/team/invitations/${item.id}`, { method: 'DELETE' });
      if (this.disposed) return;
      this.set({ revoking: null, items: this.withRow({ ...item, state: 'revoked' }), rowMessage: { id: item.id, text: 'Revoked. The link no longer works.' } });
    } catch (error) {
      if (this.disposed) return;
      const code = isApiRequestError(error) ? error.code : '';
      if (code === 'invitation_accepted') {
        return this.set({ revoking: null, items: this.withRow({ ...item, state: 'accepted' }), rowMessage: { id: item.id, text: 'Already accepted, so it can’t be revoked. Manage their access in Admin.' } });
      }
      if (isApiRequestError(error) && error.status === 404) {
        return this.set({ revoking: null, items: this.snap.items.filter(i => i.id !== item.id), rowMessage: null, listNotice: 'That invitation no longer exists.' });
      }
      const text =
        code === 'role_escalation_denied' ? 'Only an owner can revoke an owner invitation.'
          : classifyFailure(error) === 'unknown' ? 'No answer from the server. Reload the list to see whether it was revoked.'
            : 'It wasn’t revoked. Try again.';
      this.set({ revoking: null, rowMessage: { id: item.id, text } });
    }
  }

  /** Inserts or replaces a row, keeping only rows the current filter shows. */
  private withRow(view: InvitationView): InvitationView[] {
    const filter = this.snap.filter;
    const rest = this.snap.items.filter(i => i.id !== view.id);
    if (filter && view.state !== filter) return rest;
    const at = this.snap.items.findIndex(i => i.id === view.id);
    if (at === -1) return [view, ...rest];
    const copy = [...this.snap.items];
    copy[at] = view;
    return copy;
  }

  private set(patch: Partial<InvitationsSnapshot>): void {
    this.snap = { ...this.snap, ...patch };
    for (const l of [...this.listeners]) l();
  }
}
